import React, { useState } from "react";
import useTransactionListLogic, { Transaction } from "./Transactions.logic";
import { data, Link } from "react-router-dom";
import {
  DataTable,
  DataTableExpandedRows,
  DataTableFilterMeta,
  DataTableValueArray,
} from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { FilterMatchMode } from "primereact/api";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { Tooltip } from "primereact/tooltip";
import { Dialog } from "primereact/dialog";
import { classNames } from "primereact/utils";

const TransactionList: React.FC = () => {
  const {
    transactions,
    loading,
    paymentReceiptLoading,
    handleShowPaymentReceipt,
  } = useTransactionListLogic();

  const statusBodyTemplate = (rowData: Transaction) => {
    return rowData.status == "registered" ? (
      <span className="p-tag p-tag-success">{rowData.status_label}</span>
    ) : (
      <span className="p-tag p-tag-danger">{rowData.status_label}</span>
    );
  };

  const amountBodyTemplate = (rowData: Transaction) => {
    return (
      <div>
        <div>
          {rowData.donation_amount > 0 ? (
            <>
              {(rowData.amount ?? 0).toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </>
          ) : (
            "Free"
          )}
        </div>
        {rowData.donation_amount > 0 && (
          <small className="text-muted">
            Donation:{" "}
            {rowData.donation_amount.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })}
          </small>
        )}
      </div>
    );
  };

  const [globalFilterValue, setGlobalFilterValue] = useState<string>("");

  const [filters, setFilters] = useState<DataTableFilterMeta>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
  });

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    let _filters = { ...filters };

    // @ts-ignore
    _filters["global"].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const renderHeader = () => {
    return (
      <>
        <ConfirmPopup />
        <div className="flex justify-content-start align-items-center mb-2 flex-column-responsive">
          <IconField iconPosition="left">
            <InputIcon className="fas fa-search" />
            <InputText
              value={globalFilterValue}
              onChange={onGlobalFilterChange}
              placeholder="Keyword Search"
            />
          </IconField>
        </div>
      </>
    );
  };

  const header = renderHeader();

  const [expandedRows, setExpandedRows] = useState<
    DataTableExpandedRows | DataTableValueArray | undefined
  >(undefined);

  const allowExpansion = (rowData: Transaction) => {
    return rowData.payment_id !== null;
  };

  const rowExpansionTemplate = (rowData: Transaction) => {
    return (
      <div className="container-fluid py-3 row-expansion-template">
        <div className="row">
          <div className="col-12 col-md-4 mb-3 mb-md-0">
            <h3 className="fw-bold">Payment Ref.:</h3>
            <p className="mb-2">{rowData.payment_id}</p>
          </div>
          {rowData.refund_id && (
            <div className="col-12 col-md-4 mb-3 mb-md-0">
              <h3 className="fw-bold">Refund Ref.:</h3>
              <p className="mb-2">{rowData.refund_id}</p>
            </div>
          )}
          <div className="col-12 col-md-4 mb-3 mb-md-0">
            <h3 className="fw-bold">Receipt:</h3>
            <a
              className="text-decoration-underline"
              onClick={() => handleShowPaymentReceipt(rowData.Event.id ?? 0)}
            >
              {paymentReceiptLoading ? (
                <i className="fas fa-spinner fa-spin"></i>
              ) : (
                <>
                  View <i className="fas fa-external-link-alt"></i>
                </>
              )}
            </a>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <section className="schedule-three ngo_list_wrapper">
        <div className="container">
          <div className="schedule-three__top">
            <div
              className="section-title text-center wow fadeInLeft"
              data-wow-delay="100ms"
            >
              <h2 className="section-title__title">Transactions</h2>
            </div>
          </div>
          <div className="schedule-three__bottom">
            <DataTable
              value={transactions}
              expandedRows={expandedRows}
              onRowToggle={(e) => setExpandedRows(e.data)}
              rowExpansionTemplate={rowExpansionTemplate}
              removableSort
              sortField="registered_at"
              showGridlines
              paginator
              rows={5}
              loading={loading}
              rowsPerPageOptions={[5, 10, 25, 50]}
              filters={filters}
              // filterDisplay="row"
              globalFilterFields={["Event.title"]}
              header={header}
              emptyMessage="No transactions found."
              onFilter={(e) => setFilters(e.filters)}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              currentPageReportTemplate="Showing {first} to {last} of {totalRecords} Transactions"
              tableStyle={{ minWidth: "50rem" }}
            >
              <Column expander={allowExpansion} style={{ width: "5rem" }} />
              <Column field="Event.title" header="Event" sortable></Column>
              <Column
                field="Event.location_city"
                header="Location"
                sortable
              ></Column>
              <Column
                field="registered_at_formatted"
                header="Registered At"
                sortable
              ></Column>
              <Column
                field="cancelled_at_formatted"
                header="Cancelled At"
                sortable
              ></Column>
              <Column
                headerStyle={{ width: "15%", minWidth: "8rem" }}
                field="amount"
                header="Amount Paid"
                bodyStyle={{ textAlign: "center" }}
                body={amountBodyTemplate}
                headerClassName="custom-status-column"
                sortable
              ></Column>
              <Column
                headerStyle={{ width: "10%", minWidth: "8rem" }}
                field="status_label"
                header="Registration Status"
                bodyStyle={{ textAlign: "center" }}
                body={statusBodyTemplate}
                headerClassName="custom-status-column"
              ></Column>
            </DataTable>
          </div>
        </div>
      </section>
    </>
  );
};

export default TransactionList;
